import React, { useEffect, useMemo, useRef, useState } from 'react';
import HighchartsReact from 'highcharts-react-official';
import * as Highcharts from 'highcharts';
import { useTheme } from '@mui/material/styles';
import map from 'lodash-es/map';
import cloneDeep from 'lodash-es/cloneDeep';
import ChartStyleWrapper from 'components/columnChart/chartStyleWrapper';
import { SimpleSeries } from 'components/multipleLineChart/types';

export default function MultipleLineChart({
  series,
  customLegend,
  yAxisOptions,
  customTooltipFormatter,
  crosshairOptions,
}: {
  series: SimpleSeries[];
  customTooltipFormatter?: Highcharts.TooltipFormatterCallbackFunction;
  customLegend?: Highcharts.LegendOptions;
  yAxisOptions?: Highcharts.YAxisOptions;
  crosshairOptions?: Highcharts.AxisCrosshairOptions;
}) {
  const ref = useRef<HighchartsReact.RefObject | null>(null);
  const theme = useTheme();

  const memoizedCategories = useMemo(() => {
    return map(series?.[0]?.data, (item) => item?.name);
  }, [series]);

  const memoizedSeries = useMemo(() => {
    const clones = map(series, (s) => cloneDeep(s));

    return map(
      clones,
      (clone) =>
        ({
          ...{ ...clone, type: 'line' },
          colorByPoint: false,
          color: clone.color,
          cursor: 'pointer',
          lineColor: clone.color,
          marker: {
            fillColor: clone.color,
            symbol: 'circle',
            radius: 5,
          },
          lineWidth: 1.5,
        }) as Highcharts.SeriesOptionsType
    ) as Highcharts.SeriesOptionsType[];
  }, [series]);

  const crosshairMixin = useMemo(() => {
    return {
      width: 1,
      color: theme.palette.text.primary,
      ...crosshairOptions,
    } as Highcharts.AxisCrosshairOptions;
  }, [crosshairOptions, theme.palette.text.primary]);

  const chartMixin = useMemo(() => {
    return {
      backgroundColor: 'transparent',
      reflow: true,
      style: {
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
      },
    } as Highcharts.ChartOptions;
  }, [theme.palette.text.primary, theme.typography.fontFamily]);

  const tooltipParams = {
    enabled: true,
    useHTML: true,
    shared: true,
    outside: true,
    followPointer: false,
    backgroundColor: '#433A4E',
    borderRadius: 12,
    padding: 16,
    formatter: customTooltipFormatter,
  };

  const [options, setOptions] = useState<Highcharts.Options>(() => {
    return {
      chart: chartMixin,
      xAxis: {
        crosshair: crosshairMixin,
        tickmarkPlacement: 'on',
        type: 'category',
        categories: memoizedCategories,
        lineWidth: 1,
        lineColor: theme.palette.custom.fontColorNotActive,
        gridLineWidth: 1,
        gridLineColor: theme.palette.custom.fontColorNotActive,
        labels: {
          style: {
            color: 'white',
          },
        },
        scrollbar: { enabled: false },
      },
      yAxis: {
        gridLineWidth: 1,
        gridLineColor: theme.palette.custom.fontColorNotActive,
        lineWidth: 1,
        lineColor: theme.palette.custom.fontColorNotActive,
        labels: {
          style: {
            color: theme.palette.custom.fontColorNotActive,
          },
        },
        title: {
          style: {
            display: 'none',
          },
        },
        softMax: 1,
        allowDecimals: false,
        ...yAxisOptions,
      },
      series: memoizedSeries,
      legend: customLegend ? customLegend : { enabled: true },
      plotOptions: {
        series: {
          pointPlacement: 'on',
        },
      },
      tooltip: tooltipParams,
      title: {
        style: {
          display: 'none',
        },
      },
    };
  });

  useEffect(() => {
    setOptions((prevOptions) => {
      return {
        ...prevOptions,
        chart: chartMixin,
        xAxis: {
          ...prevOptions.xAxis,
          crosshair: crosshairMixin,
          categories: memoizedCategories,
        },
        series: memoizedSeries,
      };
    });
  }, [memoizedCategories, memoizedSeries, crosshairMixin, chartMixin]);

  return (
    <ChartStyleWrapper>
      <HighchartsReact ref={ref} highcharts={Highcharts} options={options} />
    </ChartStyleWrapper>
  );
}
